/**
 * Textarea — multi-line text input.
 *
 * Inherits Input visual styling. Supports `autoResize` for content-driven
 * height (uses field-sizing-content; modern browsers).
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const textareaVariants = cva(
  [
    "ui-control",
    "block w-full rounded-md bg-white dark:bg-webex-gray-950",
    "text-sm text-webex-gray-1000 dark:text-white",
    "placeholder:text-webex-gray-500 dark:placeholder:text-webex-gray-400",
    "border transition-colors duration-150 motion-reduce:transition-none",
    "focus:outline-none focus:ring-2 focus:ring-webex-blue-600 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-webex-gray-950",
    "disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-webex-gray-100 dark:disabled:bg-webex-gray-900",
    "px-3 py-2 min-h-20 resize-y leading-normal",
  ].join(" "),
  {
    variants: {
      state: {
        default: "border-webex-gray-500 dark:border-webex-gray-400",
        error:   "border-webex-red-600 dark:border-webex-red-500 focus:ring-webex-red-600",
      },
      autoResize: {
        true: "[field-sizing:content] resize-none",
        false: "",
      },
    },
    defaultVariants: {
      state: "default",
      autoResize: false,
    },
  },
)

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {}

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, state, autoResize, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(textareaVariants({ state, autoResize }), className)}
        {...props}
      />
    )
  },
)
Textarea.displayName = "Textarea"
